import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Helper to check if URL contains Supabase auth tokens
 * These tokens need to be processed by the AcceptInvite page
 */
const hasAuthTokensInUrl = (): boolean => {
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Check hash fragment for auth tokens (implicit flow)
  if (hash) {
    const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    
    if (type === 'invite' || type === 'recovery' || type === 'signup' || accessToken) {
      return true;
    }
  }
  
  // Check query params for auth tokens (PKCE flow)
  const type = searchParams.get('type');
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  
  if (type === 'invite' || type === 'recovery' || accessToken || refreshToken) {
    return true;
  }
  
  return false;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Check for auth tokens in URL - redirect to accept-invite to process them
  if (hasAuthTokensInUrl()) {
    console.log('🔐 ProtectedRoute: Detected auth tokens in URL, redirecting to /accept-invite');
    return <Navigate to={`/accept-invite${window.location.search}${window.location.hash}`} replace />;
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    console.log('🔐 ProtectedRoute: Redirecting to /login for authentication');
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
